/**
 * Company-setup onboarding state, shared by the server check and every level of
 * the indicator chain (Home checklist card → rail CTA → nav dots → section
 * badges on /org/profile). Client-safe: no Prisma, no server imports.
 *
 * Bank details and NAV credentials block invoicing, so they gate
 * `onboardingComplete` (see `getShellOrgState`). Contact details only fill
 * document footers — they appear as a checklist step but never hold onboarding
 * open, which is why an org missing *only* contact details sees no indicators.
 */

export interface OnboardingStatus {
	hasBankDetails: boolean
	hasContactDetails: boolean
	hasNavCredentials: boolean
}

/** Stable identifiers for the three checklist steps, in display order. */
export type OnboardingStepKey = 'bank' | 'contact' | 'nav'

export const ONBOARDING_STEP_KEYS: readonly OnboardingStepKey[] = ['bank', 'contact', 'nav']

/** The single destination every indicator points at. */
export const COMPANY_DATA_PATH = '/org/profile'

export function isOnboardingStepComplete(onboarding: OnboardingStatus, step: OnboardingStepKey): boolean {
	switch (step) {
		case 'bank':
			return onboarding.hasBankDetails
		case 'contact':
			return onboarding.hasContactDetails
		case 'nav':
			return onboarding.hasNavCredentials
	}
}

/** How many of the three steps are still empty — the number on the rail CTA badge. */
export function countMissingOnboardingSteps(onboarding: OnboardingStatus): number {
	return ONBOARDING_STEP_KEYS.filter(step => !isOnboardingStepComplete(onboarding, step)).length
}

export function countCompleteOnboardingSteps(onboarding: OnboardingStatus): number {
	return ONBOARDING_STEP_KEYS.length - countMissingOnboardingSteps(onboarding)
}

/**
 * Whether to render the indicator chain at all. Non-admins are excluded on
 * purpose: they cannot edit company data, so an attention dot they can never
 * clear would be pure noise.
 */
export function shouldShowSetupIndicators({
	onboardingComplete,
	isOrgAdmin,
}: {
	onboardingComplete: boolean
	isOrgAdmin: boolean
}): boolean {
	return isOrgAdmin && !onboardingComplete
}
